import React, { useMemo } from 'react';
import RouterLink from '@/components/Navigation/PublicPageLink';
import { StyleSheet, View, ScrollView, Pressable } from 'react-native';
import Text from '@/components/Typography/Text';
import Heading from '@/components/Typography/Heading';
import PublicFooter from '@/components/PublicFooter';
import PropTypes from 'prop-types';
import { useTheme } from '@/context/ThemeContext';

// Create styles outside component
const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
  },
  content: {
    width: '100%',
    maxWidth: 800,
    padding: 20,
    paddingTop: 100,
    paddingBottom: 60,
  },
  title: {
    textAlign: 'center',
    marginBottom: 6,
  },
  title2: {
    textAlign: 'center',
  },
  subtitle: {
    marginBottom: 40,
    textAlign: 'center',
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    marginBottom: 16,
  },
  list: {
    marginLeft: 20,
    marginBottom: 16,
  },
  listItem: {
    lineHeight: 24,
    marginBottom: 8,
  },
});

const createTableStyles = (colors) => StyleSheet.create({
  // Table styles
  tableContainer: {
    marginVertical: 16,
    borderWidth: 1,
    borderColor: colors.solid_color_secondary,
    borderRadius: 8,
    overflow: 'hidden',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.solid_color_secondary,
  },
  tableHeader: {
    backgroundColor: colors.solid_color_secondary,
  },
  tableHeaderCell: {
    flex: 1,
    padding: 12,
    borderRightWidth: 1,
    borderRightColor: colors.solid_color_secondary,
  },
  tableCell: {
    flex: 1,
    padding: 12,
    borderRightWidth: 1,
    borderRightColor: colors.solid_color_secondary,
  },
  lastCell: {
    borderRightWidth: 0,
  },
  lastRow: {
    borderBottomWidth: 0,
  },
});

// Memoized section component
const Section = React.memo(({ title, children }) => (
  <View style={styles.section}>
    <Heading h3 style={styles.sectionTitle}>{title}</Heading>
    {children}
  </View>
));

// Memoized list component
const List = React.memo(({ items = [] }) => (
  <View style={styles.list}>
    {(items || []).map((item, index) => (
      <Text key={index} style={styles.listItem} color='primary' size='m'>• {item}</Text>
    ))}
  </View>
));

// Memoized table component
const Table = React.memo(({ headers, rows }) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createTableStyles(colors), [colors]);

  return (
    <View style={styles.tableContainer}>
      <View style={[styles.tableRow, styles.tableHeader]}>
        {headers.map((header, index) => (
          <View
            key={index}
            style={[
              styles.tableHeaderCell,
              index === headers.length - 1 && styles.lastCell
            ]}
          >
            <Text weight="medium" color="primary">{header}</Text>
          </View>
        ))}
      </View>
      {rows.map((row, rowIndex) => (
        <View
          key={rowIndex}
          style={[
            styles.tableRow,
            rowIndex === rows.length - 1 && styles.lastRow
          ]}
        >
          {row.map((cell, cellIndex) => (
            <View
              key={cellIndex}
              style={[
                styles.tableCell,
                cellIndex === row.length - 1 && styles.lastCell
              ]}
            >
              <Text color="primary">{cell}</Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  );
});

// PropTypes for Table
Table.propTypes = {
  headers: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
};

// Memoized contact info component
const ContactInfo = React.memo(() => (
  <Section title="Contact Information">
    <Text color='primary'>
      For questions, please contact us at:{'\n\n'}
      Gab AI Inc.{'\n'}
      1143 Northern Blvd #158{'\n'}
      Clarks Summit, PA 18411{'\n'}
      United States of America{'\n\n'}
      Email: ai@gab.com
    </Text>
  </Section>
));

const LegalPage = ({ title, subtitle, lastUpdated, children }) => {

  return (
    <ScrollView style={{ flex: 1 }}>
      <View style={styles.container}>
        <View style={styles.content}>
          <Heading h1 style={styles.title}>GAB AI INC</Heading>
          <Heading h2 style={styles.title2}>{title}</Heading>
          <Text style={styles.subtitle} color='tertiary'>Last Updated: {lastUpdated}</Text>

          {children}

          <ContactInfo styles={styles} />
        </View>
      </View>
      <PublicFooter />
    </ScrollView>
  );
};

LegalPage.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  lastUpdated: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const linkStyles = StyleSheet.create({
  link: {
    alignSelf: 'flex-start',
  },
  text: {
    textDecorationLine: 'underline',
  },
});

function Link({
  children,
  href,
  onPress,
  isDisabled = false,
  ...props
}) {
  return (
    <RouterLink href={href} asChild>
      <Pressable
        disabled={isDisabled}
        style={linkStyles.link}
        {...props}
      >
        <Text
          weight='medium'
          color={!!isDisabled ? 'tertiary' : 'primary'}
          style={linkStyles.text}
        >
          {children}
        </Text>
      </Pressable>
    </RouterLink>
  );
}

export { Section, List, Link, Table };
export default React.memo(LegalPage); 